"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useTrackingData } from "@/contexts/tracking/tracking.context";
import type { TrackingDataModel } from "@/hooks/tracking/models/tracking-data-model.types";
import type { UserModel } from "@/services/api/profile/models/user.model";
import { useAuthentication } from "@/vendors/authentication/hooks/use-authentication.hook";
import { useCookiebotConsent } from "@/vendors/Cookiebot/_hooks/use-cookiebot-consent.hook";
export function useBloomreachPageView() {
  const $ = _c(8);
  const {
    trackingData
  } = useTrackingData();
  const {
    user
  } = useAuthentication();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const isTracked = useRef(false);
  const consent = useCookiebotConsent();
  let t0;
  if ($[0] !== consent || $[1] !== trackingData || $[2] !== user) {
    t0 = () => {
      if (trackingData === null || window.exponea === undefined) {
        return;
      }
      if (!shouldTrack(isTracked.current, trackingData, consent.submitted)) {
        return;
      }
      const ssoId = user.user?.ssoId ?? null;
      if (!window.exponea.isInitialized) {
        const customer = ssoId !== "anon" ? {
          sso_id: ssoId ?? undefined
        } : {};
        window.exponea.start({
          customer
        });
        setIsScriptLoaded(true);
      }
      if (window.exponea.isInitialized) {
        handleBloomreachTracking(ssoId, window.document.referrer, trackingData, user);
        isTracked.current = true;
      }
    };
    $[0] = consent;
    $[1] = trackingData;
    $[2] = user;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== consent || $[5] !== isScriptLoaded || $[6] !== trackingData) {
    t1 = [trackingData, isScriptLoaded, consent];
    $[4] = consent;
    $[5] = isScriptLoaded;
    $[6] = trackingData;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useEffect(t0, t1);
}
function shouldTrack(alreadyTracked: boolean, trackingData: TrackingDataModel | null, consentSubmitted: boolean) {
  return !alreadyTracked && typeof window.exponea !== "undefined" && trackingData !== null && consentSubmitted;
}
function handleBloomreachTracking(ssoId: string | null, referer: null | string, trackingData: TrackingDataModel, user: UserModel) {
  if (!window.exponea) {
    return;
  }
  const currentSsoId = window.exponea.configuration.customer.sso_id;
  if (currentSsoId !== undefined && currentSsoId !== ssoId) {
    window.exponea.anonymize();
  }
  const identity = ssoId !== "anon" ? {
    sso_id: ssoId ?? undefined
  } : {};
  window.exponea.identify(identity);
  window.exponea.track("page_visit", buildTrackingContext(referer, trackingData, user));
}
function getJoinedProperty<T>(items: ReadonlyArray<T>, key: keyof T): string {
  if (!items || items.length === 0) {
    return "";
  }
  return items.map(item => item[key]).join(",");
}
function buildTrackingContext(ref: null | string, trackingData: TrackingDataModel, user: UserModel) {
  const ssoId = user.user?.ssoId;
  const formattedSsoId = ssoId && ssoId !== "anon" ? ssoId : undefined;
  return {
    access_agreement_account_numbers: getJoinedProperty(user.accessAgreements, "accountNumber"),
    access_agreement_ids: getJoinedProperty(user.accessAgreements, "accessAgreementId"),
    article_locked: trackingData.pageRestricted === "yes",
    authorization_method: user.access.class !== "none" ? user.access.class : "NOTSET",
    content_id: trackingData.contentId,
    have_access: user.hasActiveSubscription,
    logged_in: user.isLoggedIn,
    page_restricted_type: trackingData.pageRestrictedType,
    referrer: ref !== null ? ref : "",
    section_name: trackingData.sectionName,
    site: trackingData.site,
    ssoId: formattedSsoId,
    subscription_account_numbers: getJoinedProperty(user.subscriptions, "accountNumber")
  };
}